import { Fragment, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Menu,
    MenuItemLink,
    usePermissions,
    useSidebarState,
} from 'react-admin';

import ArticleIcon from '@mui/icons-material/Article';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import DescriptionIcon from '@mui/icons-material/Description';
import FolderCopyOutlinedIcon from '@mui/icons-material/FolderCopyOutlined';
import SettingsIcon from '@mui/icons-material/Settings';
import QuizIcon from '@mui/icons-material/Quiz';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import ViewListOutlinedIcon from '@mui/icons-material/ViewListOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import ReviewsIcon from '@mui/icons-material/Reviews';
import StarRateRoundedIcon from '@mui/icons-material/StarRateRounded';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import VideoCameraBackOutlinedIcon from '@mui/icons-material/VideoCameraBackOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import AutoGraphOutlinedIcon from '@mui/icons-material/AutoGraphOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import GraphicEqOutlinedIcon from '@mui/icons-material/GraphicEqOutlined';
import QueryStatsOutlinedIcon from '@mui/icons-material/QueryStatsOutlined';
import DevicesOutlinedIcon from '@mui/icons-material/DevicesOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import AutoStoriesOutlinedIcon from '@mui/icons-material/AutoStoriesOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import RuleOutlinedIcon from '@mui/icons-material/RuleOutlined';
import PriceCheckOutlinedIcon from '@mui/icons-material/PriceCheckOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import RecordVoiceOverOutlinedIcon from '@mui/icons-material/RecordVoiceOverOutlined';
import HearingDisabledOutlinedIcon from '@mui/icons-material/HearingDisabledOutlined';
import TroubleshootOutlinedIcon from '@mui/icons-material/TroubleshootOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import PsychologyAltOutlinedIcon from '@mui/icons-material/PsychologyAltOutlined';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';

import {
    Box,
    Collapse,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import { useExamName } from './examName';
import { examTaskCategories, getExamTaskTypeLabel, getExamTaskTypesByExamName } from '../examTasks/examTaskTypes';
import { getExamTaskWorkflow } from '../examTasks/examTaskWorkflows';
import { getAdminChromeColor, getAdminEnvironmentKey } from './adminEnvironment';
import {
    hasSpeechCalibrationPermission,
    SPEECH_CALIBRATION_ADJUDICATOR_TAG,
    SPEECH_CALIBRATION_RATER_TAG,
    SPEECH_CALIBRATION_RELEASE_MANAGER_TAG,
} from '../speechCalibration/speechCalibrationConstants';

export function AppMenu() {
    const location = useLocation();
    const navigate = useNavigate();
    const [open] = useSidebarState();
    const { permissions } = usePermissions();
    const { examName } = useExamName();

    const envKey = getAdminEnvironmentKey();
    const bg = getAdminChromeColor();
    const reviewWorkflow = getExamTaskWorkflow('review');
    const destinationWorkflow = getExamTaskWorkflow('destination');
    // Hide review exam tasks from the menu
    const showReviewExamTasks = envKey === 'dev';
    // Always show live/destination exam task management in the admin
    const showLiveExamTasks = envKey === 'dev' || envKey === 'stage';
    // Only surface the Sync Files link when running a production build
    const showSyncFiles = envKey === 'dev' || envKey === 'stage';
    const canRateSpeech = hasSpeechCalibrationPermission(permissions, [SPEECH_CALIBRATION_RATER_TAG]);
    const canAdjudicateSpeech = hasSpeechCalibrationPermission(permissions, [SPEECH_CALIBRATION_ADJUDICATOR_TAG]);
    const canManageSpeechCalibration = hasSpeechCalibrationPermission(permissions, [SPEECH_CALIBRATION_RELEASE_MANAGER_TAG]);

    const isInReviewExamTasks = location.pathname.startsWith(reviewWorkflow.routeBase);
    const isInDestinationExamTasks = location.pathname.startsWith(destinationWorkflow.routeBase);
    const isInUltron = location.pathname.startsWith('/ultron-insights') || location.pathname.startsWith('/ultron-knowledge');

    const [reviewExamTasksOpen, setReviewExamTasksOpen] = useState<boolean>(isInReviewExamTasks);
    const [destinationExamTasksOpen, setDestinationExamTasksOpen] = useState<boolean>(isInDestinationExamTasks);
    const [ultronOpen, setUltronOpen] = useState<boolean>(isInUltron);

    useEffect(() => {
        if (isInReviewExamTasks) {
            setReviewExamTasksOpen(true);
        }
    }, [isInReviewExamTasks]);

    useEffect(() => {
        if (isInDestinationExamTasks) {
            setDestinationExamTasksOpen(true);
        }
    }, [isInDestinationExamTasks]);

    useEffect(() => {
        if (isInUltron) {
            setUltronOpen(true);
        }
    }, [isInUltron]);

    const examTaskLinksByCategory = useMemo(() => {
        const byCategory = new Map<string, ReturnType<typeof getExamTaskTypesByExamName>>();
        for (const category of examTaskCategories) byCategory.set(category.id, []);
        for (const examTaskType of getExamTaskTypesByExamName(examName)) {
            const arr = byCategory.get(examTaskType.category) ?? [];
            arr.push(examTaskType);
            byCategory.set(examTaskType.category, arr);
        }
        return byCategory;
    }, [examName]);

    const isRouteActive = (path: string) => location.pathname === path || location.pathname.startsWith(`${path}/`);

    const navItemSx = (isActive: boolean) => ({
        position: 'relative',
        mx: 0.75,
        mb: 0.5,
        minHeight: 46,
        borderRadius: open ? 3 : 3.5,
        px: open ? 1.5 : 1,
        color: '#ffffff',
        background: isActive
            ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.96) 0%, rgba(15, 23, 42, 0.92) 100%)'
            : 'transparent',
        boxShadow: isActive ? '0 16px 36px rgba(15, 23, 42, 0.28)' : 'none',
        transition: 'background-color 180ms ease, box-shadow 180ms ease, color 180ms ease, transform 180ms ease',
        ...(isActive
            ? {
                  '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: 8,
                      top: 8,
                      bottom: 8,
                      width: 4,
                      borderRadius: 999,
                      background: 'linear-gradient(180deg, #f8fafc 0%, #38bdf8 100%)',
                  },
              }
            : {}),
        '& .MuiListItemIcon-root': {
            minWidth: open ? 38 : 'auto',
            color: '#ffffff',
            transition: 'color 180ms ease',
        },
        '& .MuiListItemText-root, & .MuiListItemText-primary, & .MuiTypography-root': {
            color: '#ffffff',
            transition: 'color 180ms ease',
        },
        '& .RaMenuItemLink-active': {
            color: 'inherit',
        },
        '& .MuiListItemText-primary': {
            fontSize: open ? '0.92rem' : '0.85rem',
            fontWeight: isActive ? 700 : 600,
            letterSpacing: '0.01em',
        },
        '&:hover': {
            background: isActive
                ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.96) 0%, rgba(15, 23, 42, 0.92) 100%)'
                : alpha('#1e293b', 0.78),
            color: '#e2e8f0',
            transform: open ? 'translateX(2px)' : 'none',
            '& .MuiListItemIcon-root': {
                color: '#f8fafc',
            },
            '& .MuiListItemText-root, & .MuiListItemText-primary, & .MuiTypography-root': {
                color: '#f8fafc',
            },
        },
    });

    const examTaskLinkSx = (isActive: boolean) => ({
        ...navItemSx(isActive),
        mx: 1,
        mb: 0.25,
        minHeight: 40,
        pl: open ? 5.5 : 1,
        pr: 1.25,
        ...(isActive
            ? {
                  '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: open ? 22 : 8,
                      top: 10,
                      bottom: 10,
                      width: 3,
                      borderRadius: 999,
                      background: 'linear-gradient(180deg, #67e8f9 0%, #22d3ee 100%)',
                  },
              }
            : {}),
        '& .MuiListItemText-primary': {
            fontSize: '0.82rem',
            fontWeight: isActive ? 700 : 500,
            lineHeight: 1.25,
        },
    });

    const renderPrimaryLink = (to: string, label: string, icon: React.ReactElement) => (
        <MenuItemLink
            to={to}
            primaryText={label}
            leftIcon={icon}
            sx={navItemSx(isRouteActive(to))}
        />
    );

    const renderExamTaskSection = (
        sectionLabel: string,
        routeBase: string,
        isSelected: boolean,
        isExpanded: boolean,
        setExpanded: React.Dispatch<React.SetStateAction<boolean>>,
    ) => (
        <Box
            sx={{
                position: 'relative',
                mb: 1.25,
                pb: isExpanded && open ? 1 : 0,
                borderBottom: isExpanded && open ? '1px solid rgba(148, 163, 184, 0.12)' : '1px solid transparent',
            }}
        >
            <ListItemButton
                onClick={() => setExpanded((value) => !value)}
                selected={isSelected}
                sx={[
                    navItemSx(isSelected),
                    { py: 1 },
                    !open ? { justifyContent: 'center' } : null,
                ]}
            >
                <ListItemIcon sx={{ minWidth: open ? 40 : 'auto' }}>
                    <QuizIcon />
                </ListItemIcon>
                {open ? <ListItemText primary={sectionLabel} /> : null}
                {open ? (isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />) : null}
            </ListItemButton>

            <Collapse in={isExpanded && open} timeout="auto" unmountOnExit>
                <Box sx={{ pt: 0.25, pb: 0.75 }}>
                    {examTaskCategories.map((category) => (
                        <Fragment key={`${routeBase}-${category.id}`}>
                            <Box sx={{ px: 3.5, pt: 1.25, pb: 0.5 }}>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        display: 'block',
                                        color: 'rgba(255, 255, 255, 0.82)',
                                        fontWeight: 800,
                                        letterSpacing: '0.14em',
                                        textTransform: 'uppercase',
                                    }}
                                >
                                    {category.label}
                                </Typography>
                            </Box>
                            {(examTaskLinksByCategory.get(category.id) ?? []).map((examTaskType) => (
                                <MenuItemLink
                                    key={`${routeBase}-${examTaskType.key}`}
                                    to={`${routeBase}/${examTaskType.key}`}
                                    primaryText={getExamTaskTypeLabel(examTaskType, examName)}
                                    leftIcon={<Fragment />}
                                    sx={examTaskLinkSx(isRouteActive(`${routeBase}/${examTaskType.key}`))}
                                />
                            ))}
                        </Fragment>
                    ))}
                </Box>
            </Collapse>
        </Box>
    );

    const renderUltronSection = () => (
        <Box
            sx={{
                position: 'relative',
                mb: 1.25,
                pb: ultronOpen && open ? 1 : 0,
                borderBottom: ultronOpen && open ? '1px solid rgba(148, 163, 184, 0.12)' : '1px solid transparent',
            }}
        >
            <ListItemButton
                onClick={() => {
                    if (open) {
                        setUltronOpen((value) => !value);
                    } else {
                        navigate('/ultron-insights');
                    }
                }}
                selected={isInUltron}
                sx={[
                    navItemSx(isInUltron),
                    { py: 1 },
                    !open ? { justifyContent: 'center' } : null,
                ]}
            >
                <ListItemIcon sx={{ minWidth: open ? 40 : 'auto' }}>
                    <SmartToyOutlinedIcon />
                </ListItemIcon>
                {open ? <ListItemText primary="Ultron" /> : null}
                {open ? (ultronOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />) : null}
            </ListItemButton>

            <Collapse in={ultronOpen && open} timeout="auto" unmountOnExit>
                <Box sx={{ pt: 0.25, pb: 0.75 }}>
                    <MenuItemLink
                        to="/ultron-insights"
                        primaryText="Ultron Insights"
                        leftIcon={<Fragment />}
                        sx={examTaskLinkSx(isRouteActive('/ultron-insights'))}
                    />
                    <MenuItemLink
                        to="/ultron-knowledge"
                        primaryText="Knowledge"
                        leftIcon={<Fragment />}
                        sx={examTaskLinkSx(isRouteActive('/ultron-knowledge'))}
                    />
                </Box>
            </Collapse>
        </Box>
    );

    return (
        <Menu
            sx={{
                height: '100%',
                overflowY: 'auto',
                overflowX: 'hidden',
                px: 1,
                py: 1.5,
                scrollbarWidth: 'thin',
                background:
                    `radial-gradient(circle at top, rgba(30, 41, 59, 0.6) 0%, rgba(15, 23, 42, 0) 32%), linear-gradient(180deg, ${bg} 0%, ${bg} 100%)`,
                color: '#e2e8f0',
                '& .RaMenu-list': {
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: '100%',
                    gap: 0.25,
                    paddingBottom: 2,
                },
                '&::-webkit-scrollbar': {
                    width: 10,
                },
                '&::-webkit-scrollbar-thumb': {
                    borderRadius: 999,
                    backgroundColor: 'rgba(148, 163, 184, 0.35)',
                    border: '2px solid transparent',
                    backgroundClip: 'padding-box',
                },
            }}
        >
            {open ? (
                <Box
                    sx={{
                        mx: 1,
                        mb: 1.25,
                        border: '1px solid rgba(148, 163, 184, 0.18)',
                        borderRadius: 4,
                        px: 2,
                        py: 1.5,
                        background: 'linear-gradient(180deg, rgba(15, 23, 42, 0.9) 0%, rgba(15, 23, 42, 0.6) 100%)',
                        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05)',
                    }}
                >
                    <Typography sx={{ color: '#f8fafc', fontSize: '0.95rem', fontWeight: 800, letterSpacing: '0.02em' }}>
                        PTE Ultra Admin
                    </Typography>
                    <Typography sx={{ mt: 0.35, color: '#94a3b8', fontSize: '0.78rem', lineHeight: 1.4 }}>
                        Operations, content, and exam task workflows.
                    </Typography>
                </Box>
            ) : null}

            {renderPrimaryLink('/business-overview', 'Business Overview', <DashboardOutlinedIcon />)}
            {renderPrimaryLink('/special-database', 'Special Database', <StorageOutlinedIcon />)}
            {renderPrimaryLink('/practice-trends', 'Practice Trends', <QueryStatsOutlinedIcon />)}
            {renderPrimaryLink('/practice-history-analysis', 'Practice History Analysis', <HistoryOutlinedIcon />)}
            {renderPrimaryLink('/scoring-observability', 'Scoring Observability', <TroubleshootOutlinedIcon />)}
            {canRateSpeech ? renderPrimaryLink('/speech-calibration', 'Speech Calibration', <ScienceOutlinedIcon />) : null}
            {canAdjudicateSpeech
                ? renderPrimaryLink('/speech-calibration/adjudication', 'Speech Adjudication', <GavelOutlinedIcon />)
                : null}
            {canManageSpeechCalibration
                ? renderPrimaryLink('/speech-calibration/readiness', 'Speech Readiness', <VerifiedOutlinedIcon />)
                : null}
            {renderPrimaryLink('/user-ratings', 'User Ratings', <StarRateRoundedIcon />)}
            {renderPrimaryLink('/usage-insights', 'Usage Insights', <DevicesOutlinedIcon />)}
            {renderPrimaryLink('/security', 'Security', <SecurityOutlinedIcon />)}
            {renderPrimaryLink('/payment-overview', 'Payment Overview', <PaymentsOutlinedIcon />)}
            {renderPrimaryLink('/blogPages', 'Blog Pages', <ArticleIcon />)}
            {renderPrimaryLink('/blog-tags', 'Blog Tags', <SellOutlinedIcon />)}
            {renderPrimaryLink('/static-contents', 'Static Pages', <DescriptionIcon />)}
            {renderPrimaryLink('/home-page-contents', 'Home Page Contents', <HomeOutlinedIcon />)}
            {renderPrimaryLink('/quick-read-aloud-attempts', 'Quick Read Aloud', <RecordVoiceOverOutlinedIcon />)}
            {renderPrimaryLink('/user-notifications', 'User Notifications', <NotificationsActiveOutlinedIcon />)}
            {renderPrimaryLink('/templates', 'Templates', <FolderCopyOutlinedIcon />)}
            {renderPrimaryLink('/vocabulary', 'Vocabulary', <AutoStoriesOutlinedIcon />)}
            {renderUltronSection()}
            {renderPrimaryLink('/billing/features', 'Subscriptions', <ReceiptLongIcon />)}
            {renderPrimaryLink('/mock-tests', 'Mock Tests', <FactCheckIcon />)}
            {renderPrimaryLink('/mock-test-diagnosis', 'Mock Test Diagnosis', <PriceCheckOutlinedIcon />)}
            {renderPrimaryLink('/question-diagnosis', 'Question Diagnosis', <RuleOutlinedIcon />)}
            {renderPrimaryLink('/describe-image-preprocessing', 'AI Source Preprocessing', <TroubleshootOutlinedIcon />)}
            {renderPrimaryLink('/voice-playback-issues', 'Voice Playback Issues', <HearingDisabledOutlinedIcon />)}
            {renderPrimaryLink('/mock-test-generation-profiles', 'Mock Test Profiles', <ViewListOutlinedIcon />)}
            {renderPrimaryLink('/mock-test-score-review', 'Mock Test Score Review', <AssessmentOutlinedIcon />)}
            {renderPrimaryLink('/marking-configuration/task-configs', 'Marking Configuration', <TuneOutlinedIcon />)}
            {renderPrimaryLink('/study-plan-algorithm/profiles', 'Study Plan Algorithm', <AutoGraphOutlinedIcon />)}
            {renderPrimaryLink('/social-proof-settings', 'Social Proof Settings', <CampaignOutlinedIcon />)}
            {renderPrimaryLink('/contact-us', 'Contact Us', <ContactSupportIcon />)}
            {renderPrimaryLink('/feedback', 'Feedback', <ReviewsIcon />)}
            {renderPrimaryLink('/ai-score-feedback', 'AI Score Feedback', <PsychologyAltOutlinedIcon />)}
            {renderPrimaryLink('/admin-users', 'Users', <PeopleOutlineIcon />)}
            {renderPrimaryLink('/flag-reports', 'Flag Reports', <FlagOutlinedIcon />)}
            {renderPrimaryLink('/photo-approval', 'Photo Approval', <PhotoCameraIcon />)}

            {showReviewExamTasks
                ? renderExamTaskSection(
                      reviewWorkflow.menuLabel,
                      reviewWorkflow.routeBase,
                      isInReviewExamTasks,
                      reviewExamTasksOpen,
                      setReviewExamTasksOpen,
                  )
                : null}

            {showLiveExamTasks
                ? renderExamTaskSection(
                      destinationWorkflow.menuLabel,
                      destinationWorkflow.routeBase,
                      isInDestinationExamTasks,
                      destinationExamTasksOpen,
                      setDestinationExamTasksOpen,
                  )
                : null}

            {showSyncFiles ? renderPrimaryLink('/live-exam-task-sync-files', 'Sync Files', <SyncOutlinedIcon />) : null}

            <Box sx={{ mt: 'auto', pt: 1.5 }}>
                {renderPrimaryLink('/audio-generate', 'Audio Generate', <GraphicEqOutlinedIcon />)}
                {renderPrimaryLink('/video-generate', 'Video Generate', <VideoCameraBackOutlinedIcon />)}
                {renderPrimaryLink('/settings', 'Project Settings', <SettingsIcon />)}
            </Box>
        </Menu>
    );
}
